import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Search, User, MapPin, CheckCircle2, UserPlus } from 'lucide-react';
import SaakhSetuLoader from '@/components/SaakhSetuLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SelectFarmer = ({ journeyType, onFarmerSelected, preselectedFarmerId }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [previousAssessment, setPreviousAssessment] = useState(null);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [autoSelected, setAutoSelected] = useState(false);

  useEffect(() => {
    fetchFarmers();
  }, []);

  // Auto-select farmer when returning from farmer creation
  useEffect(() => {
    if (preselectedFarmerId && farmers.length > 0 && !autoSelected) {
      const match = farmers.find(f => f.id === preselectedFarmerId);
      if (match) {
        selectFarmer(match);
        setAutoSelected(true);
      }
    }
  }, [preselectedFarmerId, farmers, autoSelected]);

  const fetchFarmers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/farmers`, { headers: { Authorization: `Bearer ${token}` } });
      setFarmers(res.data);
    } catch {
      toast.error('Failed to load farmers');
    } finally {
      setLoading(false);
    }
  };

  const selectFarmer = async (farmer) => {
    setSelectedFarmer(farmer);
    setLoadingAssessments(true);
    try {
      const res = await axios.get(`${API}/assessments/farmer/${farmer.id}`);
      const assessments = res.data.assessments || [];
      const latest = assessments.find(a => a.assessment_status === 'SCORED' || a.assessment_status === 'APPROVED');
      setPreviousAssessment(latest || null);
    } catch {
      setPreviousAssessment(null);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const confirmSelection = () => {
    if (selectedFarmer) {
      onFarmerSelected(selectedFarmer, previousAssessment);
    }
  };

  const isRenewalJourney = journeyType === 'SAME_LIMIT_RENEWAL' || journeyType === 'ENHANCED_LIMIT';

  const filtered = farmers.filter(f =>
    f.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.aadhaar_number?.includes(searchTerm) ||
    f.contact_number?.includes(searchTerm)
  );

  if (loading) {
    return <SaakhSetuLoader message="Loading farmers..." />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="select-farmer-title">
          {isRenewalJourney ? 'Select Existing Farmer' : 'Select or Create Farmer'}
        </h3>
        <p className="text-sm text-gray-500">
          {isRenewalJourney
            ? 'Search for the farmer and confirm their previous loan cycle'
            : 'Search for an existing farmer or create a new profile for assessment'}
        </p>
      </div>

      {/* Search + Create New */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, Aadhaar, or contact..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="farmer-search-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/farmers/new?returnTo=assessment&journeyType=${journeyType}`)}
          className="shrink-0"
          data-testid="create-new-farmer-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" /> New Farmer
        </Button>
      </div>

      {/* Farmer List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">No farmers found</div>
        ) : (
          filtered.map(f => (
            <div
              key={f.id}
              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                selectedFarmer?.id === f.id
                  ? 'border-blue-500 bg-blue-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => selectFarmer(f)}
              data-testid={`farmer-option-${f.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{f.farmer_name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                      <span>****{f.aadhaar_number?.slice(-4)}</span>
                      <span className="text-gray-300">|</span>
                      <MapPin className="h-3 w-3" />
                      <span>{f.residential_district || 'N/A'}, {f.residential_state || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {(f.yogyank_score_base ?? f.yogyank_score_final) != null && (
                    <div className="text-sm font-semibold" style={{ color: (f.yogyank_score_base ?? f.yogyank_score_final) >= 500 ? '#16a34a' : '#dc2626' }}>
                      Yogyank: {f.yogyank_score_base ?? f.yogyank_score_final}
                    </div>
                  )}
                  <div className="text-xs text-gray-400">{f.total_land_holding_size_acres} acres</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Selected Farmer Details + Previous Assessment */}
      {selectedFarmer && (
        <Card className="border-blue-200 bg-blue-50/50" data-testid="selected-farmer-detail">
          <CardContent className="pt-4">
            {loadingAssessments ? (
              <div className="text-center py-4 text-sm text-gray-500">Loading assessment history...</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">{selectedFarmer.farmer_name}</span>
                </div>

                {previousAssessment ? (
                  <div className="bg-white rounded-md p-3 border border-blue-100 space-y-1.5">
                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Previous Cycle</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="text-gray-500">Journey Type</div>
                      <div className="font-medium">{previousAssessment.journey_type}</div>
                      <div className="text-gray-500">Cycle</div>
                      <div className="font-medium">{previousAssessment.cycle_number}</div>
                      <div className="text-gray-500">Yogyank</div>
                      <div className="font-medium">{previousAssessment.yogyank_score_base ?? previousAssessment.yogyank_score_final ?? 'N/A'}</div>
                      <div className="text-gray-500">Sanctioned Limit</div>
                      <div className="font-medium">
                        {previousAssessment.sanctioned_limit_approved
                          ? `Rs. ${Number(previousAssessment.sanctioned_limit_approved).toLocaleString()}`
                          : 'N/A'}
                      </div>
                      <div className="text-gray-500">Scored On</div>
                      <div className="font-medium">{previousAssessment.scoring_date?.slice(0, 10) || 'N/A'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                    No previous scored assessment found for this farmer.
                    {isRenewalJourney && ' A previous cycle is recommended for renewal/enhancement workflows.'}
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={confirmSelection}
                  style={{ background: '#2563eb' }}
                  data-testid="confirm-farmer-selection-btn"
                >
                  Confirm & Continue
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SelectFarmer;
